import { ButtonCus, ButtonSearch } from '@/components/common/Button';
import Combobox from '@/components/common/Combobox';
import {
    Form,
    FormControl,
    FormField,
    FormItem
} from '@/components/common/Form';
import { Input } from '@/components/common/Input';
import TableActionButtons from '@/components/common/TableActionButtons';
import Role from '@/components/general/Role';
import Status from '@/components/general/Status';
import Table from '@/components/table/Table';
import {
    DEFAULT_SELECT_PAGE_SIZES,
    ROLE_EK_OPTION_SELECT,
    ROLE_GAS_OPTION_SELECT,
    ACC_STATUS_OPTION_SELECT,
    ROUTES,
    DRIVER_LICENSE_STATUS_SELECT,
    ekDomain
} from '@/configs/constants';
import { useStore } from '@/hooks/useStore';
import yup from '@/services/yup';
import { Account } from '@/types/account';
import { isPathEk } from '@/utils/routes';
import { yupResolver } from '@hookform/resolvers/yup';
import { ColumnDef, TableState, Updater } from '@tanstack/react-table';
import { CirclePlus } from 'lucide-react';
import { toJS } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import ExpiredDriverLicenseIcon from '@/assets/images/expired-driver-license-icon.png';
import SubAdultIcon from '@/assets/images/sub-adult-icon.svg';
import Tooltip from '@/components/common/Tooltip';
import { cn } from '@/utils/utils';

export default observer(function AccountManagementPage() {
    // hook
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { pathname } = useLocation();

    const isEk = isPathEk(pathname);

    // store
    const {
        accountStore: {
            getAccountsPagination,
            accountsPagination,
            totalRecord,
            paging,
            defaultPaging,
            keepSearchParams,
            setObservable
        },
        modalStore: { hideAllModals }
    } = useStore();

    // form
    // validate schema
    const validateSchema = yup.object().shape({
        roleId: yup.string().nullable(),
        status: yup.number().nullable(),
        keyword: yup.string().trim(),
        licenseStatus: yup.boolean()
    });

    type FormData = yup.InferType<typeof validateSchema>;

    const form = useForm<FormData>({
        resolver: yupResolver(validateSchema),
        mode: 'onSubmit',
        defaultValues: {
            roleId: keepSearchParams?.roleId || undefined,
            status: keepSearchParams?.status || undefined,
            keyword: keepSearchParams?.keyword || '',
            licenseStatus: keepSearchParams?.licenseStatus || undefined
        }
    });

    const roles = (isEk ? ROLE_EK_OPTION_SELECT : ROLE_GAS_OPTION_SELECT);

    // state
    const [rowSelected, setRowSelected] = useState({});

    // lifecycle
    useEffect(() => {
        getAccountsPagination(keepSearchParams || {}, paging);
    }, []);

    // function
    const onSearch = (searchParams?: FormData) => {
        const params: any = { ...searchParams };
        getAccountsPagination(params, defaultPaging);
        searchParams && setObservable('keepSearchParams', toJS(searchParams));
        hideAllModals();
    };

    const onStateChange = (stateUpdater: Updater<TableState>) => {
        const newPaging =
            stateUpdater instanceof Function ? stateUpdater(paging) : stateUpdater;
        getAccountsPagination(keepSearchParams, newPaging);
    };

    const onClickEdit = (accountId: number) => {
        const path = `${ROUTES.userManagement.href}/${ROUTES.userEdit.href}/${accountId}`;
        navigate(isEk ? `/${ekDomain}${path}` : path);
    };

    // columns
    const columns: ColumnDef<Account>[] = [
        {
            id: 'id',
            accessorKey: 'id',
            header: '',
            enableSorting: false,
            size: 60,
            cell: ({ row: { original } }) => (
                <TableActionButtons onView={() => onClickEdit(original.id)} />
            )
        },
        {
            accessorKey: 'licenseImage',
            header: '',
            size: 105,
            enableSorting: false,
            cell: ({ row: { original } }) => {
                const imagePath = original?.licenseBeforeImage?.path || '';
                return (
                    <div className='max-w-[70px] min-w-[70px]'>
                        {imagePath?.length > 0 && (
                            <img
                                className='max-w-[70px] max-h-[50px] object-contain'
                                src={imagePath}
                                alt={t('field.bike_image')}
                            />
                        )}
                    </div>
                );
            }
        },
        {
            accessorKey: 'userCode',
            header: t('field.user_code'),
            size: 100,
            enableSorting: false,
            cell: ({ row: { original } }) => {
                return (
                    <div className='min-w-[80px]'>
                        {original?.userCode ?? ''}
                    </div>
                );
            }
        },
        {
            accessorKey: 'isLicenseExpired_isUnderage',
            header: '',
            size: 60,
            meta: {
                headerClassName: 'px-0',
                cellClassName: 'px-0'
            },
            enableSorting: false,
            cell: ({ row: { original } }) => {
                const showCell: boolean = (!isEk && original?.isLicenseExpired) || original?.isUnderage;
                return (
                    showCell ?
                        (
                            <div className='flex items-center justify-center gap-1'>
                                {!isEk && original?.isLicenseExpired && (
                                    <Tooltip content={t('sentences.expired_license')} triggerAsChild>
                                        <span className='min-w-7 cursor-pointer'>
                                            <img src={ExpiredDriverLicenseIcon} alt='' className='size-7' />
                                        </span>
                                    </Tooltip>
                                )}
                                {original?.isUnderage && (
                                    <Tooltip content={t('sentences.underage')} triggerAsChild>
                                        <span className='min-w-[22px] cursor-pointer'>
                                            <img src={SubAdultIcon} alt='' className='w-[22px]' />
                                        </span>
                                    </Tooltip>
                                )}
                            </div>
                        ) :
                        (
                            <></>
                        )
                );
            }
        },
        {
            accessorKey: 'fullname',
            header: t('field.name_or_company_name'),
            size: 150,
            minSize: 150,
            enableSorting: false,
            cell: ({ row: { original } }) => (
                <div className='min-w-[130px] break-all line-clamp-2'>
                    {original?.fullname || ''}
                </div>
            )
        },
        {
            accessorKey: 'furiganaName',
            header: t('field.furigana_name'),
            size: 280,
            enableSorting: false,
            cell: ({ row: { original } }) => (
                <div className='min-w-[100px] break-all line-clamp-2'>
                    {original?.furiganaName || ''}
                </div>
            )
        },
        {
            accessorKey: 'age',
            header: t('field.age'),
            size: 40,
            enableSorting: false,
            meta: {
                headerClassName: 'right-sub-header-item',
                cellClassName: ''
            },
            cell: ({ row: { original } }) => {
                const ageText = original?.age?.toString() === '0' ?
                    '0' :
                    (original?.age ?? '');

                return (
                    <div className='text-right'>{ageText}</div>
                );
            }
        },
        {
            accessorKey: 'phone',
            header: t('field.phone_number'),
            size: 280,
            enableSorting: false,
            cell: ({ row: { original } }) => {
                const phoneNumber = original.phone?.toString()?.trim();
                return (
                    <div className='min-w-[100px]'>
                        {phoneNumber?.length > 0 && (
                            <a href={`tel:${phoneNumber}`} className='link-normal'>
                                {phoneNumber}
                            </a>
                        )}
                    </div>
                );
            }
        },
        {
            accessorKey: 'email',
            header: t('field.email'),
            size: 280,
            enableSorting: false,
            cell: ({ row: { original } }) => {
                const email = original.email?.toString()?.trim();
                return (
                    <div className='min-w-[100px]'>
                        {email?.length > 0 && (
                            <a href={`mailto:${email}`} className='link-normal'>
                                {email}
                            </a>
                        )}
                    </div>
                );
            }
        },
        {
            accessorKey: 'roleId',
            header: t('field.role'),
            size: 240,
            enableSorting: false,
            cell: ({ row: { original } }) => (
                <div className='min-w-[100px]'>
                    <Role roleId={original?.role?.id} />
                </div>
            )
        },
        {
            accessorKey: 'status',
            header: t('field.status'),
            size: 200,
            enableSorting: false,
            cell: ({ row: { original } }) => (
                <div className='min-w-[100px]'>
                    <Status status={original.status} />
                </div>
            )
        }
    ];

    return (
        <>
            <div className='layout-card'>
                <Form {...form}>
                    <form onSubmit={form.handleSubmit(onSearch)}>
                        <div className={cn(
                            'grid grid-cols-1 xsm:grid-cols-2 gap-6',
                            isEk ? 'lg:grid-cols-3' : 'xl:grid-cols-4'
                        )}
                        >
                            <FormField
                                control={form.control}
                                name='roleId'
                                render={({ field }) => (
                                    <FormItem>
                                        <FormControl>
                                            <Combobox
                                                search={false}
                                                placeholder={t('field.role')}
                                                value={field.value?.toString() ?? ''}
                                                onChange={(item) => {
                                                    field.onChange(item?.code ?? '');
                                                }}
                                                items={roles.map(item => ({
                                                    label: t(`${item.name}`),
                                                    value: item.id.toString(),
                                                    code: item.id
                                                }))}
                                            />
                                        </FormControl>
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name='status'
                                render={({ field }) => (
                                    <FormItem>
                                        <FormControl>
                                            <Combobox
                                                search={false}
                                                placeholder={t('words_title.active_inactive_status')}
                                                value={
                                                    field.value === 0 ?
                                                        '0' :
                                                        (field.value || '').toString()
                                                }
                                                onChange={(item) => {
                                                    field.onChange(item?.value);
                                                    form.setValue('status', item?.code);
                                                }}
                                                items={ACC_STATUS_OPTION_SELECT.map(item => ({
                                                    label: t(`${item.name}`),
                                                    value: item.id.toString(),
                                                    code: item.id
                                                }))}
                                            />
                                        </FormControl>
                                    </FormItem>
                                )}
                            />
                            {!isEk && (
                                <FormField
                                    control={form.control}
                                    name='licenseStatus'
                                    render={({ field }) => (
                                        <FormItem className='col-span-full sm:col-span-1'>
                                            <FormControl>
                                                <Combobox
                                                    search={false}
                                                    placeholder={t(
                                                        'driver_license.driver_license_status'
                                                    )}
                                                    value={field.value?.toString() ?? ''}
                                                    onChange={(item) => {
                                                        field.onChange(item?.value);
                                                        form.setValue('licenseStatus', item?.code);
                                                    }}
                                                    items={DRIVER_LICENSE_STATUS_SELECT.map(item => ({
                                                        label: t(`${item.name}`),
                                                        value: item.value.toString(),
                                                        code: item.value.toString()
                                                    }))}
                                                />
                                            </FormControl>
                                        </FormItem>
                                    )}
                                />
                            )}
                            <FormField
                                control={form.control}
                                name='keyword'
                                render={({ field }) => (
                                    <FormItem className={cn('col-span-full sm:col-span-1')}>
                                        <FormControl>
                                            <Input
                                                type='text'
                                                placeholder={t('field.email_name_phone')}
                                                {...field}
                                            />
                                        </FormControl>
                                    </FormItem>
                                )}
                            />
                            <div className='col-span-full flex justify-center'>
                                <ButtonSearch size='md' className='max-w-52'>
                                    {t('button.search')}
                                </ButtonSearch>
                            </div>
                        </div>
                    </form>
                </Form>
            </div>

            <div className='layout-card mt-6'>
                <Table
                    columns={columns}
                    data={accountsPagination}
                    state={{
                        ...paging,
                        rowSelection: rowSelected
                    }}
                    rowCount={totalRecord}
                    onStateChange={onStateChange}
                    selectPageSizes={DEFAULT_SELECT_PAGE_SIZES}
                    enableRowSelection={false}
                    onRowSelectionChange={setRowSelected}
                    getRowId={row => row.id?.toString()}
                    renderActions={(
                        <Link
                            to={
                                isPathEk(pathname) ?
                                    `/${ekDomain}${ROUTES.userManagement.href}/${ROUTES.userCreate.href}` :
                                    `${ROUTES.userManagement.href}/${ROUTES.userCreate.href}`
                            }
                        >
                            <ButtonCus variant='default' size='md'>
                                <CirclePlus className='mr-2' />
                                {t('button.add_new')}
                            </ButtonCus>
                        </Link>
                    )}
                />
            </div>
        </>
    );
});
